import { Button, EllipsisSpan, Paging, Table, Tag, Tooltip, UsePagingProps } from '@maxtropy/components';
import { V2LabelConsumptionStatisticsPagePostResponse } from '@maxtropy/device-customer-apis-v2';
import { Space } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import ShowDetailModal from './ShowDetailModal';
import { keepTwoNull } from '@/utils/utils';
import { MeterType } from '@/api/energyConsumption';
import { RangeValue } from '../..';
import { DateType } from '@/pages/EnergyLabel/utils';

export interface IEnergyTable {
  data?: V2LabelConsumptionStatisticsPagePostResponse['list'];
  loading?: boolean;
  timeResolution?: DateType;
  pagingInfo: UsePagingProps;
  selectedConsumption?: number;
  displayValue?: RangeValue;
  dataQueryPeriodId?: number;
}

export type ListItemProps = Exclude<V2LabelConsumptionStatisticsPagePostResponse['list'], undefined>[number];

const EnergyTable: FC<IEnergyTable> = props => {
  const { data, loading, timeResolution, pagingInfo, selectedConsumption, displayValue, dataQueryPeriodId } = props;

  const [openModal, setOpenModal] = useState(false); // 查看详情弹窗
  const [record, setRecord] = useState<ListItemProps>(); // 每行数据

  const renderUseEnergy = useCallback(
    (record: ListItemProps) => {
      if (selectedConsumption === MeterType.ELECTRICAL_ENERGY) {
        return (
          <Tooltip
            title={
              <>
                <span>总：{keepTwoNull(record.totalValue)}</span>
                <br />
                <span>尖：{keepTwoNull(record.summitValue)}</span>
                <br />
                <span>峰：{keepTwoNull(record.peakValue)}</span>
                <br />
                <span>平：{keepTwoNull(record.plainValue)}</span>
                <br />
                <span>谷：{keepTwoNull(record.valleyValue)}</span>
              </>
            }
          >
            <Tag border="solid" color="default">
              总：{keepTwoNull(record.totalValue)}
            </Tag>
            <Tag border="solid" color="#FF9247">
              尖：{keepTwoNull(record.summitValue)}
            </Tag>
            <Tag border="solid" color="#CE90D1">
              峰：{keepTwoNull(record.peakValue)}
            </Tag>
            <Tag border="solid" color="#52E7FF">
              平：{keepTwoNull(record.plainValue)}
            </Tag>
            <Tag border="solid" color="#57FB8B">
              谷：{keepTwoNull(record.valleyValue)}
            </Tag>
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title={<span>总：{keepTwoNull(record.totalValue)}</span>}>
            <Tag border="solid" color="default">
              总：{keepTwoNull(record.totalValue)}
            </Tag>
          </Tooltip>
        );
      }
    },
    [selectedConsumption]
  );

  const columns = useMemo(() => {
    return [
      {
        title: '用能单元',
        width: 240,
        dataIndex: 'energyUnitName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '所属标签',
        width: 240,
        dataIndex: 'labelName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: `能耗数据（${data?.[0]?.physicalUnitName ?? '-'}）`,
        dataIndex: 'total',
        ellipsis: { showTitle: true },
        render: (v: number, record: ListItemProps) => renderUseEnergy(record),
      },
    ];
  }, [selectedConsumption, data]);

  // 表格操作栏
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      fixed: 'right' as 'right',
      width: 100,
      render: (record: ListItemProps) => (
        <Space size={16}>
          <Button
            type="link"
            onClick={() => {
              setOpenModal(true);
              setRecord(record);
            }}
          >
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey={record => `${record.energyUnitId}-${record.labelId}`}
        sticky
        scroll={{ x: 1300 }}
        loading={loading}
        columns={buildColumns}
        dataSource={data}
        pagination={false}
      />
      <Paging pagingInfo={pagingInfo} />
      {openModal && (
        <ShowDetailModal
          timeResolution={timeResolution}
          displayValue={displayValue}
          dataQueryPeriodId={dataQueryPeriodId}
          energyData={record!}
          selectedConsumption={selectedConsumption}
          onCancel={() => {
            setOpenModal(false);
            setRecord(undefined);
          }}
        />
      )}
    </>
  );
};

export default EnergyTable;
